import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { useNavigate, useParams } from 'react-router-dom';
import {getAPIData, adminGetCompanies, updateAPIReportData} from '../../api/api'
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { DataGrid, Column, HeaderFilter,
  Paging, Editing, Grouping, Lookup,
  Button as DGButton,
} from 'devextreme-react/data-grid';
import { Chart, Series, Legend, ValueAxis, Tooltip,
  AggregationInterval, ArgumentAxis, Aggregation, Tick,
  TickInterval, CommonSeriesSettings, } from 'devextreme-react/chart';

import { Button } from 'devextreme-react/button';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid as xlsExportDataGrid } from 'devextreme/excel_exporter';
import { SelectBox } from 'devextreme-react/select-box';
import './hse.scss';
import { groupBy } from '../../utils/miscUtil';

export default function HSECompanyData() {
  let { year } = useParams();
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companiesList, setCompaniesList] = useState(null);
  const [yearsList, setYearsList] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryGraphData, setSummaryGraphData] = useState(null);
  const [values, setValues] = useState([]);

  const calculateCount = useCallback((aggregationInfo) => {
    console.log(aggregationInfo);
    if (!aggregationInfo.data.length) {
      return null;
    }
    var total = 0;
    var fd = groupBy(aggregationInfo.data, 'status')
    for (const [key] of Object.entries(fd)) {
      total = total + fd[key].length
      fd[key] = fd[key].length
    }

    fd['total']=total;
    fd['date'] = aggregationInfo.intervalStart;
    console.log(fd);
    return fd;
  }, []);

  const customizeTooltip = useCallback((pointInfo) => {
    let y = pointInfo.argument;
    var txt = ''
    if (pointInfo.total !== undefined) {
      txt = `<div><div class="tooltip-header">Total companies: ${pointInfo.total}</div><div class="tooltip-body">`
      for (var i=0; i<pointInfo.points.length; i++) {
        txt = txt+`<div>${pointInfo.points[i].seriesName}: ${pointInfo.points[i].valueText} (${pointInfo.points[i].percentText})</div>`
      }
      txt=txt+`</div></div>`
    }
    return {
      html: txt,
    }
  }, []);

  const onYearSelected = useCallback((e) => {
    setCurrentYear(e.value);
  }, [])

  const onGetCompaniesSuccess = useCallback((data) => {
    data = data.sort((a,b) => (a.company_name > b.company_name) ? 1 : ((b.company_name > a.company_name) ? -1 : 0))
    setCompaniesList(data);
    if (yearsList.length === 0) {
      const currentYear = new Date().getFullYear();
      for (var i=2023; i<currentYear; i++) {
        yearsList.push(i.toString())
      }
      setYearsList([...yearsList]);
    }
    if (year) {
      onYearSelected({value: year.toString()});
    }
    setLoading(false);
  }, [setCompaniesList, setLoading, yearsList, setYearsList, onYearSelected, year])

  const onGetSummaryDataSuccess = useCallback((data) => {
    let cl = groupBy(companiesList, 'company_id');
    for (var i=0; i<data.length; i++) {
      data[i]['company_name'] = cl[data[i]['company_id']][0]['company_name']
      data[i]['created_at'] = Date.parse(data[i]['created_at'])
      data[i]['updated_at'] = Date.parse(data[i]['updated_at'])
      cl[data[i]['company_id']][0]['visited_'+data[i]['reporting_year']] = true
    }

    for (const [key] of Object.entries(cl)) {
      if (cl[key][0]['visited_'+currentYear]) {
        continue
      }
      data.push({company_id: cl[key][0].company_id, company_name: cl[key][0].company_name, status: "Not Submitted", reporting_year:currentYear})
    }
    setSummaryData(data);
    let fd = data.map((it) => { return {date: it['reporting_year'], status: it['status'] || "Not Submitted"}})
    setSummaryGraphData(fd)
    console.log(fd);


  }, [currentYear, companiesList]);

  const onError = useCallback((error) => {
    if (error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
      setLoading(false);
    }
  }, [refreshSession]);



  useEffect(() => {
    if (user.signInUserSession) {
      let groups = user.signInUserSession.idToken.payload['cognito:groups']
      var has_perm=false;
      if (groups) {
        for (var i=0; i<groups.length; i++) {
          if (groups[i]==='admin') {
            has_perm=true;
            setIsAdmin(true);
            if (companiesList === null) {
              adminGetCompanies(user, onGetCompaniesSuccess, onError);
            }
            break;
          }
        }
      }
      if (!has_perm) {
        notify("Insufficient permissions. Please contact the system administrator.", 'error', 5000);
        navigate('/home');
        return
      }


    }
  }, [user, navigate, onGetCompaniesSuccess, onError, setIsAdmin, companiesList]);

  useEffect(() => {
    if (currentYear) {
      getAPIData("/reports/summary/hse/"+currentYear, user, onGetSummaryDataSuccess, onError);
    }
  }, [currentYear, onGetSummaryDataSuccess, onError, user]);


  const onExporting = useCallback((e) => {
    if (e.format === "xlsx") {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      xlsExportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'AXPC-SIF-Events.xlsx');
        });
      });
    }
  }, []);

  const onUpdateSummarySuccess = useCallback((e) => {
    notify("Data updated succesfully.", 'success', 5000);
    getAPIData("/reports/summary/hse/"+currentYear, user, onGetSummaryDataSuccess, onError);
  }, [user, onGetSummaryDataSuccess, onError, currentYear])

  const onSaving = useCallback((e) => {
    for (var i=0; i< e.changes.length; i++) {
      if (e.changes[i].type!=="update") {
        continue
      }
      var data = {...e.changes[i].key}
      data['reporting_year'] = currentYear.toString();
      data[Object.entries(e.changes[i].data)[0][0]] = Object.entries(e.changes[i].data)[0][1]
      e.promise = updateAPIReportData("/reports/summary/hse/", user, data, onUpdateSummarySuccess, onError)
      e.cancel = true
      break;
    }
  }, [user, currentYear, onUpdateSummarySuccess, onError])

  if(!loading) {
    return (
      <React.Fragment>
        <div className="d-flex flex-row justify-content-between">
          <h2 className={'content-block'}>Health, Safety & Environmental Company Data</h2>
          <div className="d-flex flex-row justify-content-between">
            <Button
              className="me-2"
              text="Data Upload"
              stylingMode="outlined"
              type="default"
              onClick={() => {navigate("/hseupload")}}
            />
          </div>
        </div>
        <hr className="mb-4"/>
        {isAdmin && <div className="mb-2">
        <h5 className="mb-2"> Choose the reporting year</h5>
        <SelectBox
            dataSource={yearsList}
            showDropDownButton={true}
            activeStateEnabled={true}
            onValueChanged={onYearSelected}
            value={currentYear}
        >
        </SelectBox>
        </div>
        }

        {isAdmin && currentYear && <div className={'content-block'}>
          <Chart
            id="chart"
            width="100%"
            dataSource={summaryGraphData}
            title={"Reporting summary"}
            rotated={true}
            barGroupWidth={40}
            height={"250px"}
          >
            <ArgumentAxis visible={false}>
              <AggregationInterval years={1} />

              <Tick visible={false} />
            </ArgumentAxis>

            <CommonSeriesSettings
              argumentField="date"
            >
              <Aggregation
                enabled={true}
                calculate={calculateCount}
                method="custom"
              />
            </CommonSeriesSettings>

            <Series
              type="stackedbar"
              valueField={"Approved"}
              name={"Approved"}
              ignoreEmptyPoints={true}
              color="rgba(20, 77, 109, 1)"
            />
            <Series
              type="stackedbar"
              valueField={"Under Review"}
              name={"Under Review"}
              ignoreEmptyPoints={true}
              color="rgba(92, 194, 215, 1)"
            />
            <Series
              type="stackedbar"
              valueField={"Not Submitted"}
              name={"Not Submitted"}
              ignoreEmptyPoints={true}
              color="rgba(236, 80, 32, 1)"
            />

            <ValueAxis showZero={true} />
            <Legend
              visible={true}
              rowCount={1}
              verticalAlignment="bottom"
              horizontalAlignment="center"/>
            <Tooltip
              enabled={true}
              shared={true}
              customizeTooltip={customizeTooltip}
            />
          </Chart>

          {summaryData ? <DataGrid id="dataGrid"
            dataSource={summaryData}
            columnHidingEnabled={true}
            cellHintEnabled={true}
            wordWrapEnabled={true}
            onSaving={onSaving}
            onExporting={onExporting}
          >
            <HeaderFilter visible={true} />
            <Grouping autoExpandAll={false} />
            <Column
              dataField={'updated_at'}
              allowEditing={false}
              allowSorting={true}
              visibleIndex={0}
              dataType="datetime"
              width="190px"
            />
            <Column
              dataField={'company_name'}
              defaultSortOrder="asc"
              allowEditing={false}
              visibleIndex={1}
              width="200px"
            />
            <Column
              dataField={'notes'}
              visibleIndex={3}
              width="30%"
            />
            <Column
              dataField={'status'}
              visibleIndex={2}
              width="150px"
            >
              <Lookup
                dataSource={['Not Submitted', 'Under Review', 'Approved']}
              />
            </Column>
            <Column type="buttons" visibleIndex={30}>
              <DGButton
                  text="Upload data"
                  icon="fas fa-file-import fs-6"
                  hint="Upload data"
                  onClick={(e) => {
                    navigate("/hseupload/"+e.row.data.reporting_year+"/"+e.row.data.company_id)
                  }}
              />

              <DGButton
                  text="Company data"
                  icon="far fa-file-alt fs-6"
                  hint="Company data"
                  onClick={(e) => {

                    navigate("/hsecompanydata/"+e.row.data.reporting_year+"/"+e.row.data.company_id)
                  }}
              />

            </Column>
            <Editing
              mode="cell"
              allowUpdating={true}
              allowDeleting={false}
              allowAdding={false} />
            <Paging defaultPageSize={200} />
          </DataGrid>:
          <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
              <LoadIndicator
                  id="large-indicator"
                  height={60}
                  width={60}
              />
          </div>
          }
        </div>}

      </React.Fragment>
    )
  } else {
    return (
      <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
          <LoadIndicator
              id="large-indicator"
              height={60}
              width={60}
          />
      </div>
    )
  }

}
